import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";

import { PageHeader, Button } from "@cofano/cck";

import { intl } from "LocaleWrapper";
import Helmet from "components/helmet/Helmet";
import { Language } from "imports/Utils";
import usePurchaseCharterBlock from "./dossier/purchasecharter/usePurchaseCharterBlock";
import useContractBlock from "./dossier/contract/useContractBlock";
import ParentContractCharterAlert from "./components/ParentContractCharterAlert";
import VoyageDetailsWithTabs from "view/voyages/components/details/VoyageInfo";
import useTabs from "components/hooks/useTabs";
import VoyageLocalSelect from "view/voyages/components/selects/VoyageLocalSelect";
import CharterDocuments from "../charters/sections/CharterDocument";
import { GetPurchaseCharterDocument, GetPurchaseCharterQuery } from "__generated__/view/charters/graphql/charter.queries.generated";
import { PurchaseCharterVoyagesFragment } from "__generated__/view/charters/graphql/charter.fragments.generated";
import { GetVoyageDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import "./styling/styling.scss";

const enum PurchaseCharterTabs {
	DETAILS = "Details",
	PRICING = "Pricing",
	VOYAGES = "Voyages",
	DOCUMENTS = "Documents"
}

export type PurchaseCharterFromQuery = NonNullable<GetPurchaseCharterQuery["purchaseCharter"]>;
export type PurchaseCharterVoyage = NonNullable<PurchaseCharterVoyagesFragment["voyages"]>[number];

const PurchaseCharterDetails = () => {

	const params = useParams();
	const navigate = useNavigate();

	const [ dossier, setDossier ] = useState<{ charter: PurchaseCharterFromQuery | undefined }>({
		charter: undefined,
	});

	const [ selectedVoyageId, setSelectedVoyageId ] = useState<number | undefined>(undefined);

	const { selectedTab, selectTab } = useTabs<PurchaseCharterTabs>({
		defaultSelected: PurchaseCharterTabs.DETAILS,
		param: "purchase-charter-tab",
	});

	const [ getPurchaseCharter, { refetch, loading: dossierLoading } ] = useLazyQuery(GetPurchaseCharterDocument, {
		onCompleted: (response) => {
			const charter = response.purchaseCharter;
			setDossier({
				charter: charter,
			});
			if (charter.voyages?.[0]?.id) {
				setSelectedVoyageId(charter.voyages[0].id);
			}
		},
	});

	const loading = !!(dossierLoading || (params.id && !dossier.charter));

	const voyages = dossier.charter?.voyages || [];
	const isParentCharter = dossier.charter?.contract?.isParentContract === true;

	const { purchaseCharterBlock, purchaseCharterInfoBlock, purchaseCharterVoyagesBlock } = usePurchaseCharterBlock({
		charter: dossier.charter,
		loading: loading,
		refetch: refetch,
		onDelete: () => {
			navigate("/view/charters");
			setDossier({
				charter: undefined,
			});
		},
		onSelect: (id) => {
			setSelectedVoyageId(id);
			selectTab(PurchaseCharterTabs.VOYAGES);
		},
	});

	const { contractBlock, agreementsBlock } = useContractBlock({
		charter: dossier.charter,
		contract: dossier.charter?.contract ?? undefined,
		loading: loading,
		refetch: refetch,
		isOtherCharter: false,
	});

	useEffect(() => {
		if (params.id) {
			getPurchaseCharter({
				variables: {
					id: Number(params.id),
				},
			});
		}
	}, [ params.id ]);

	return (
		<div className="purchase-charter-details">
			<PageHeader>
				<PageHeader.Title>
					{Language("PURCHASE_CHARTER")}
					<Helmet title={intl.formatMessage({ id: "PURCHASE_CHARTER" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/charters")}>
						{Language("PURCHASE_CHARTERS")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			{purchaseCharterInfoBlock}

			<div>
				<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 3 }}>
					<Tabs value={selectedTab} onChange={(_, newValue) => selectTab(newValue)}>
						<Tab label={Language("DETAILS")} value={PurchaseCharterTabs.DETAILS} disabled={!dossier.charter} />
						<Tab label={Language("PRICING")} value={PurchaseCharterTabs.PRICING} disabled={!dossier.charter?.contract} />
						<Tab label={Language("VOYAGES")} value={PurchaseCharterTabs.VOYAGES} disabled={!dossier.charter || voyages.length === 0} />
						<Tab label={Language("DOCUMENTS")} value={PurchaseCharterTabs.DOCUMENTS} disabled={!dossier.charter} />
					</Tabs>
				</Box>
			</div>

			{selectedTab === PurchaseCharterTabs.DETAILS && (
				<Grid container spacing={2}>
					{isParentCharter && (
						<Grid size={{ xs: 12 }}><ParentContractCharterAlert /></Grid>
					)}
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>{purchaseCharterBlock}</Grid>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>{contractBlock}</Grid>
					{!isParentCharter && (
						<Grid size={12}>
							{purchaseCharterVoyagesBlock}
						</Grid>
					)}
				</Grid>
			)}

			{selectedTab === PurchaseCharterTabs.PRICING && dossier.charter && (
				<Grid container spacing={2}>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						{agreementsBlock}
					</Grid>
				</Grid>
			)}

			{selectedTab === PurchaseCharterTabs.VOYAGES && selectedVoyageId && (
				<div>
					<VoyageTab
						voyages={voyages}
						selectedVoyageId={selectedVoyageId}
						onVoyageChange={setSelectedVoyageId}
						refetchCharter={refetch} />
				</div>
			)}

			{selectedTab === PurchaseCharterTabs.DOCUMENTS && dossier.charter && (
				<div>
					<CharterDocuments charter={dossier.charter} />
				</div>
			)}
		</div>
	);
};

const VoyageTab = ({ voyages, selectedVoyageId, onVoyageChange, refetchCharter }: {
	voyages: PurchaseCharterVoyage[],
	selectedVoyageId: number,
	onVoyageChange: (option: number | undefined) => void,
	refetchCharter: () => void
}) => {
	const { data, refetch: refetchVoyage } = useQuery(GetVoyageDocument, {
		variables: { id: selectedVoyageId },
		skip: !selectedVoyageId,
	});

	const refetch = () => {
		refetchVoyage();
		refetchCharter();
	};

	return (
		<Box>
			{data?.voyage ? (
				<VoyageDetailsWithTabs
					tabSuffix={voyages.length > 1 && (
						<Box
							sx={{
								mr: 1,
								width: "250px",
							}}
						>
							<VoyageLocalSelect
								voyages={voyages}
								value={voyages.find(option => option.id === selectedVoyageId)}
								onChange={(option) => onVoyageChange?.(option?.id)}
							/>
						</Box>
					)}
					voyage={data.voyage}
					refetch={refetch}
					tabsQueryParameterName="voyage-tab"
				/>
			) : (
				<CircularProgress size={24} />
			)}
		</Box>
	);
};

export default PurchaseCharterDetails;
